import { Box, Group, Skeleton, Stack, useMantineTheme } from '@mantine/core';
import type { FloatingPosition } from '@mantine/core/lib/Floating';
import { useListCustomProperties } from '@repo/api-codegen';
import RichTooltip from '@repo/common/components/RichTooltip/RichTooltip';
import { EntityType } from '@repo/common/enums/entityType';
import { memo, useMemo } from 'react';
import type { ISecodaEntity } from '../../api';
import { CustomPropertyRendererV2 } from '../../components/CustomPropertyRenderV2';
import { SelectableProperty } from '../../components/EntityPageLayout/EntityPropertySidebar';
import IntegrationLogo from '../../components/IntegrationLogo/IntegrationLogo';

export const CustomPropertiesController = memo(
	({
		entity,
		maxWidth,
		position,
		withinPortal,
	}: {
		entity: ISecodaEntity;
		maxWidth?: number;
		position?: FloatingPosition;
		withinPortal?: boolean;
	}) => {
		const theme = useMantineTheme();

		const { data: customProperties, isLoading: isLoadingCustomProperties } =
			useListCustomProperties({});

		const filteredCustomProperties = useMemo(
			() =>
				customProperties?.filter(
					(cp) =>
						cp.entity_types.includes(entity.entity_type) ||
						cp.entity_types.includes(EntityType.all)
				),
			[customProperties, entity.entity_type]
		);

		return (
			<>
				{isLoadingCustomProperties && (
					<Stack spacing="xs">
						{[1, 2, 3].map((i) => (
							<Group key={i} noWrap align="center" spacing={0}>
								<Box miw={theme.other.space[32] + theme.other.space[1]}>
									<Skeleton height={20} width={100} />
								</Box>
								<Skeleton height={20} sx={{ flexGrow: 1 }} />
							</Group>
						))}
					</Stack>
				)}
				{filteredCustomProperties?.map((cp) => (
					<Group key={cp.id} grow>
						<SelectableProperty
							label={cp.name}
							customTooltip={
								cp.integration && cp.integration.id === entity.integration ? (
									<Box
										p="xs"
										style={{
											height: '20px',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<RichTooltip
											maxWidth={theme.other.space[60]}
											title={`Custom Property comes from ${cp.integration.name}`}
											body={`This custom property is synced from ${cp.integration.name}.`}
											linkLabel={`${cp.integration.name} settings`}
											linkUrl={`/integrations/${cp.integration.id}`}
										>
											<IntegrationLogo
												integrationType={cp.integration.type}
												size={theme.other.iconSize.sm}
											/>
										</RichTooltip>
									</Box>
								) : undefined
							}
							dataTestId={`custom-property-${cp.name}`}
						>
							<Box
								w="100%"
								ml="xs"
								style={
									maxWidth !== null ? { maxWidth: `${maxWidth}px` } : undefined
								}
							>
								<CustomPropertyRendererV2
									customProperty={cp}
									entity={entity}
									hideOnEmpty={false}
									position={position}
									withinPortal={withinPortal}
								/>
							</Box>
						</SelectableProperty>
					</Group>
				))}
			</>
		);
	}
);
